import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { fetchDictionaryTerms, toggleDictionaryBookmark } from './isoDictionaryApi';
import type { DictionaryListParams, DictionaryListResult } from './types';

const cacheKey = (params: DictionaryListParams): string =>
  JSON.stringify({
    standard: params.standard ?? 'all',
    q: params.q ?? '',
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });

export const loadDictionaryTerms = createAsyncThunk(
  'isoDictionary/loadTerms',
  async (params: DictionaryListParams = {}) => fetchDictionaryTerms(params),
);

export const toggleDictionaryBookmarkThunk = createAsyncThunk(
  'isoDictionary/toggleBookmark',
  async (termId: string) => toggleDictionaryBookmark(termId),
);

interface IsoDictionaryState {
  cache: Record<string, DictionaryListResult | undefined>;
  lastParams: DictionaryListParams;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  bookmarkStatus: 'idle' | 'loading' | 'failed';
  error: string | null;
}

const initialState: IsoDictionaryState = {
  cache: {},
  lastParams: {},
  status: 'idle',
  bookmarkStatus: 'idle',
  error: null,
};

const isoDictionarySlice = createSlice({
  name: 'isoDictionary',
  initialState,
  reducers: {
    setDictionaryParams(state, action: { payload: DictionaryListParams }) {
      state.lastParams = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDictionaryTerms.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadDictionaryTerms.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.lastParams = action.meta.arg ?? {};
        state.cache[cacheKey(action.meta.arg ?? {})] = action.payload;
      })
      .addCase(loadDictionaryTerms.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Error al cargar diccionario';
      })
      .addCase(toggleDictionaryBookmarkThunk.pending, (state) => {
        state.bookmarkStatus = 'loading';
      })
      .addCase(toggleDictionaryBookmarkThunk.fulfilled, (state, action) => {
        state.bookmarkStatus = 'idle';
        const { termId, bookmarked } = action.payload;
        const key = cacheKey(state.lastParams);
        const cached = state.cache[key];
        if (!cached) return;
        cached.items = cached.items.map((item) =>
          item.id === termId ? { ...item, bookmarked } : item,
        );
      })
      .addCase(toggleDictionaryBookmarkThunk.rejected, (state) => {
        state.bookmarkStatus = 'failed';
      });
  },
});

export const { setDictionaryParams } = isoDictionarySlice.actions;

export const selectDictionaryResult =
  (params: DictionaryListParams) => (state: RootState) =>
    state.isoDictionary.cache[cacheKey(params)];

export const selectDictionaryStatus = (state: RootState) => state.isoDictionary.status;

export const selectDictionaryBookmarkStatus = (state: RootState) =>
  state.isoDictionary.bookmarkStatus;

export default isoDictionarySlice.reducer;
